import { FieldOptionBase } from 'module/api/field/types';
import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { InputField, Select } from '@rocketlane/ui';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { SharedConfigurationBar } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/configuration-bar';
import { SectionHeading } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-heading';
import {
  SectionCard,
  SectionContent,
  SectionDivider,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { SharedPromptInputProps } from './types';
import type { Option } from '@rocketlane/ui';

export const SharedDetailBlockPromptInput = ({
  prompt,
  setPrompt,
  onGenerate,
  isGenerating,
  onCancel,
  onUndo,
  fields,
  allowResolve,
  defaultTitleKey,
  getNodeAsPayload,
  titlePlaceholderKey,
  fieldLabelKey,
  pageType,
  pageTriggerListFilter,
}: SharedPromptInputProps) => {
  const [form] = Form.useForm<{
    fields: FieldOptionBase<any>[];
    title: string;
  }>();
  const titleRef = useRef<HTMLInputElement>(null);

  const titlePlaceholder = useTranslation(titlePlaceholderKey as any);
  const isDisabled =
    isGenerating || !prompt.title?.trim() || !prompt.fields?.length;

  useEffect(() => {
    form.setFieldsValue({
      fields: prompt.fields,
      title: prompt.title,
    });
  }, [form, prompt]);

  useEffect(() => {
    queueMacrotask(() => {
      titleRef.current?.select();
    });
  }, []);

  const setFields = (value: FieldOptionBase<any>[]) => {
    setPrompt({ ...prompt, fields: value });
  };

  return (
    <SectionCard>
      <SectionHeading
        title={defaultTitleKey}
        onClose={onUndo}
        showCloseButton={!isDisabled}
      />
      <SectionContent>
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label={
              <FormattedMessage id="rich_text_editor.sections.question_section.title" />
            }
          >
            <InputField
              type="text"
              ref={titleRef}
              placeholder={titlePlaceholder}
              onChange={(e) => setPrompt({ ...prompt, title: e.target.value })}
            />
          </Form.Item>
          <Form.Item
            name="fields"
            label={<FormattedMessage id={fieldLabelKey as any} />}
          >
            <Select
              isMulti={true}
              value={prompt.fields as Option[]}
              variant="basic"
              options={fields as Option[]}
              closeMenuOnSelect={false}
              onChange={(value) => setFields(value as FieldOptionBase<any>[])}
            />
          </Form.Item>
        </Form>
        <SectionDivider />
        <SharedConfigurationBar
          prompt={prompt}
          setPrompt={setPrompt}
          isActionLoading={isGenerating}
          onCancel={onCancel}
          onSubmit={onGenerate}
          validation={{
            isDisabled,
          }}
          allowResolve={allowResolve || false}
          actionBarConfig={{
            canShowPreviewButton: false,
            canWrapTrashButton: true,
            canShowStreaming: false,
          }}
          getNodeAsPayload={getNodeAsPayload}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      </SectionContent>
    </SectionCard>
  );
};
